import React from 'react';
import styled from 'styled-components';
import { FormProvider, FormProviderProps } from './FormProvider';
import AppButton from '../ui/AppButton';
import { useFormContext } from './FormProvider';

interface FormProps<T extends Record<string, any>> extends FormProviderProps<T> {
  className?: string;
  submitLabel?: string;
  showSubmitButton?: boolean;
  submitButtonProps?: Partial<React.ComponentProps<typeof AppButton>>;
  onCancel?: () => void;
  cancelLabel?: string;
  showCancelButton?: boolean;
  buttonAlignment?: 'left' | 'center' | 'right' | 'space-between';
}

function FormContent<T extends Record<string, any>>({
  children,
  className,
  submitLabel = 'Submit',
  showSubmitButton = true,
  submitButtonProps,
  onCancel,
  cancelLabel = 'Cancel',
  showCancelButton = false,
  buttonAlignment = 'right',
}: Omit<FormProps<T>, keyof FormProviderProps<T>> & { children?: React.ReactNode }) {
  const { handleSubmit, isSubmitting, errors } = useFormContext<T>();
  const isValid = Object.keys(errors).length === 0;

  return (
    <StyledForm onSubmit={handleSubmit} className={className}>
      <FormFields>{children}</FormFields>
      
      {(showSubmitButton || showCancelButton) && (
        <ButtonContainer $alignment={buttonAlignment}>
          {showCancelButton && onCancel && (
            <AppButton
              title={cancelLabel}
              onClick={onCancel}
              disabled={isSubmitting}
              {...submitButtonProps}
            />
          )}
          {showSubmitButton && (
            <AppButton
              title={submitLabel}
              type="submit"
              onClick={() => {}} // Type submit will handle form submission
              disabled={isSubmitting || !isValid}
              {...submitButtonProps}
            />
          )}
        </ButtonContainer>
      )}
    </StyledForm>
  );
}

export function Form<T extends Record<string, any>>({
  children,
  initialValues,
  validationSchema,
  onSubmit,
  validateOnChange,
  validateOnBlur,
  resetOnSubmit,
  ...formProps
}: FormProps<T>) {
  return (
    <FormProvider
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnChange={validateOnChange}
      validateOnBlur={validateOnBlur}
      resetOnSubmit={resetOnSubmit}
    >
      <FormContent {...formProps}>{children}</FormContent>
    </FormProvider>
  );
}

// Field group component for better organization
export const FieldGroup: React.FC<{
  children: React.ReactNode;
  label?: string;
  required?: boolean;
}> = ({ children, label, required }) => {
  return (
    <FieldGroupContainer>
      {label && (
        <FieldLabel>
          {label}
          {required && <RequiredStar>*</RequiredStar>}
        </FieldLabel>
      )}
      {children}
    </FieldGroupContainer>
  );
};

// Row component for horizontal field layout
export const FieldRow: React.FC<{
  children: React.ReactNode;
  gap?: number;
}> = ({ children, gap = 16 }) => {
  return <FieldRowContainer $gap={gap}>{children}</FieldRowContainer>;
};

// Styled components
const StyledForm = styled.form`
  width: 100%;
`;

const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 24px;
`;

const ButtonContainer = styled.div<{ $alignment: string }>`
  display: flex;
  gap: 12px;
  justify-content: ${({ $alignment }) => {
    switch ($alignment) {
      case 'left':
        return 'flex-start';
      case 'center':
        return 'center';
      case 'space-between':
        return 'space-between';
      default:
        return 'flex-end';
    }
  }};
  margin-top: 24px;
`;

const FieldGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const RequiredStar = styled.span`
  color: #ff4444;
  margin-left: 4px;
`;

const FieldRowContainer = styled.div<{ $gap: number }>`
  display: flex;
  gap: ${({ $gap }) => $gap}px;
  align-items: flex-start;
  
  > * {
    flex: 1;
  }
`;